import React, { useState, useEffect } from 'react';
import { Accessibility, Plus, Minus, Maximize2, Eye, Circle, Keyboard, X } from 'lucide-react';

const AccessibilityMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState({
    fontSize: 100,
    highContrast: false,
    linkHighlight: false,
    stopAnimations: false,
    keyboardNav: false
  });

  // טעינת הגדרות מ-localStorage
  useEffect(() => {
    const saved = localStorage.getItem('accessibilitySettings');
    if (saved) {
      setSettings(JSON.parse(saved));
    }
  }, []);

  // שמירת הגדרות ל-localStorage
  useEffect(() => {
    localStorage.setItem('accessibilitySettings', JSON.stringify(settings));
    applySettings();
  }, [settings]);

  const applySettings = () => {
    const root = document.documentElement;
    
    // גודל טקסט
    root.style.fontSize = `${settings.fontSize}%`;
    
    // ניגודיות גבוהה
    if (settings.highContrast) {
      root.classList.add('high-contrast');
    } else {
      root.classList.remove('high-contrast');
    }
    
    // הדגשת קישורים
    if (settings.linkHighlight) {
      root.classList.add('link-highlight');
    } else {
      root.classList.remove('link-highlight');
    }
    
    // עצירת אנימציות
    if (settings.stopAnimations) {
      root.classList.add('no-animations');
    } else {
      root.classList.remove('no-animations');
    }
  };

  const increaseFontSize = () => {
    setSettings(prev => ({
      ...prev,
      fontSize: Math.min(prev.fontSize + 10, 150)
    }));
  };

  const decreaseFontSize = () => {
    setSettings(prev => ({
      ...prev,
      fontSize: Math.max(prev.fontSize - 10, 80)
    }));
  };

  const resetSettings = () => {
    setSettings({
      fontSize: 100,
      highContrast: false,
      linkHighlight: false,
      stopAnimations: false,
      keyboardNav: false
    });
  };

  const toggleSetting = (key) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <>
      {/* כפתור נגישות צף */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-4 left-4 z-[9999] bg-blue-600 hover:bg-blue-700 text-white rounded-full p-3 shadow-lg transition-all duration-300 hover:scale-110"
        aria-label="תפריט נגישות"
        title="נגישות"
      >
        <Accessibility className="w-6 h-6" />
      </button>

      {/* תפריט נגישות */}
      {isOpen && (
        <div className="fixed bottom-20 left-4 z-[9999] bg-white rounded-lg shadow-2xl border border-gray-200 w-80 max-h-[80vh] overflow-y-auto">
          {/* כותרת */}
          <div className="bg-blue-600 text-white p-4 rounded-t-lg flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Accessibility className="w-5 h-5" />
              <h3 className="font-bold text-lg">הצהרת נגישות</h3>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="hover:bg-blue-700 rounded-full p-1"
              aria-label="סגור"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* תוכן */}
          <div className="p-4 space-y-4">
            {/* גודל טקסט */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                גודל טקסט: {settings.fontSize}%
              </label>
              <div className="flex gap-2">
                <button
                  onClick={decreaseFontSize}
                  className="flex-1 flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-800 py-2 px-4 rounded-lg transition-colors"
                  aria-label="הקטן טקסט"
                >
                  <Minus className="w-4 h-4" />
                  הקטן
                </button>
                <button
                  onClick={increaseFontSize}
                  className="flex-1 flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-800 py-2 px-4 rounded-lg transition-colors"
                  aria-label="הגדל טקסט"
                >
                  <Plus className="w-4 h-4" />
                  הגדל
                </button>
              </div>
            </div>

            {/* ניגודיות גבוהה */}
            <button
              onClick={() => toggleSetting('highContrast')}
              className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                settings.highContrast ? 'bg-blue-100 border-2 border-blue-600' : 'bg-gray-100 hover:bg-gray-200'
              }`}
            >
              <div className="flex items-center gap-2">
                <Eye className="w-5 h-5" />
                <span className="font-medium">ניגודיות גבוהה</span>
              </div>
              <Circle className={`w-5 h-5 ${settings.highContrast ? 'fill-blue-600 text-blue-600' : 'text-gray-400'}`} />
            </button>

            {/* הדגשת קישורים */}
            <button
              onClick={() => toggleSetting('linkHighlight')}
              className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                settings.linkHighlight ? 'bg-blue-100 border-2 border-blue-600' : 'bg-gray-100 hover:bg-gray-200'
              }`}
            >
              <div className="flex items-center gap-2">
                <Maximize2 className="w-5 h-5" />
                <span className="font-medium">הדגשת קישורים</span>
              </div>
              <Circle className={`w-5 h-5 ${settings.linkHighlight ? 'fill-blue-600 text-blue-600' : 'text-gray-400'}`} />
            </button>

            {/* עצירת אנימציות */}
            <button
              onClick={() => toggleSetting('stopAnimations')}
              className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                settings.stopAnimations ? 'bg-blue-100 border-2 border-blue-600' : 'bg-gray-100 hover:bg-gray-200'
              }`}
            >
              <div className="flex items-center gap-2">
                <Circle className="w-5 h-5" />
                <span className="font-medium">עצור אנימציות</span>
              </div>
              <Circle className={`w-5 h-5 ${settings.stopAnimations ? 'fill-blue-600 text-blue-600' : 'text-gray-400'}`} />
            </button>

            {/* איפוס הגדרות */}
            <button
              onClick={resetSettings}
              className="w-full bg-gray-800 hover:bg-gray-900 text-white py-2 px-4 rounded-lg transition-colors font-medium"
            >
              אפס הגדרות
            </button>

            {/* טקסט משפטי */}
            <div className="text-xs text-gray-600 border-t pt-3">
              <p>
                אתר זה מספק כלי נגישות לפי תקן ישראלי ת"י 5568. 
                לשאלות נגישות: <a href="mailto:Jeromecarss@gmail.com" className="text-blue-600 underline">Jeromecarss@gmail.com</a>
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AccessibilityMenu;
